<template>
  <section class="def-slider" :class="{ 'def-slider_vertical': vertical }">
    <div class="def-slider__container" :class="{ 'def-slider__container_mb': pagination }">
      <div v-if="nav" ref="prev" class="def-slider__nav def-slider__prev">
        <slot name="nav-prev">
          <span class="def-slider__default-button">
            <svg><use xlink:href="#svg-arrow-left"></use></svg>
          </span>
        </slot>
      </div>
      <div ref="slider" class="swiper-container">
        <div class="swiper-wrapper">
          <slot />
        </div>
      </div>
      <div v-if="nav" ref="next" class="def-slider__nav def-slider__next">
        <slot name="nav-next">
          <span class="def-slider__default-button">
            <svg><use xlink:href="#svg-arrow-right"></use></svg>
          </span>
        </slot>
      </div>
    </div>
    <!-- If we need scrollbar -->
    <div v-if="scrollbar" ref="scrollbar" class="swiper-scrollbar"></div>
    <!-- If we need pagination -->
    <div
      v-if="pagination"
      ref="pagination"
      class="swiper-pagination"
      :class="{ 'def-slider__pagination': $slots.default && $slots.default.length > 1 }"
    ></div>
  </section>
</template>

<script>
import { markRaw } from 'vue';
import Swiper from 'swiper';
import { Navigation, Pagination /* Scrollbar */ } from 'swiper/modules';

export default {
  name: 'DefSlider',
  props: ['settings', 'scrollbar', 'nav', 'pagination', 'vertical', 'priority'],
  emits: ['ready'],
  data() {
    return {
      slider: null,
      activeIndex: 0,
      slidesCount: 0,
    };
  },
  updated() {
    if (!this.slider || this.slider.destroyed) return;

    this.slider.update();
  },
  mounted() {
    // page speed
    if (this.priority) {
      this.createSlider();
    } else if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          this.createSlider();
        },
        { timeout: 2000 }
      );
    } else {
      setTimeout(this.createSlider, 800);
    }
  },
  beforeUnmount() {
    if (!this.slider || this.slider.destroyed) return;

    this.slider.destroy(true, false);
    this.slider = null;
  },
  methods: {
    createSlider() {
      if (!this.$refs.slider) return;

      // markRaw: Swiper instance must not be wrapped in Vue reactive proxy
      this.slider = markRaw(
        new Swiper(this.$refs.slider, {
          autoHeight: false,
          observer: false,
          observeParents: false,
          modules: [Navigation, Pagination /* Scrollbar */],
          navigation: {
            nextEl: this.$refs.next,
            prevEl: this.$refs.prev,
          },
          pagination: {
            el: this.$refs.pagination,
            clickable: true,
            dynamicBullets: true,
          },
          /* scrollbar: {
            el: this.$refs.scrollbar,
            draggable: true,
          }, */
          ...this.settings,
        })
      );

      this.slider.on('slideChange', ({ activeIndex }) => {
        this.activeIndex = activeIndex;
      });

      this.activeIndex = this.slider?.activeIndex;
      this.slidesCount = this.slider?.slides?.length;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (!this.slider) return;

          this.slider.update();

          requestAnimationFrame(() => {
            this.$emit('ready', this.slider);
          });
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.def-slider {
  &_vertical {
    .def-slider__nav {
      left: 50%;
      transform: rotate(90deg) translateY(50%);
    }
    .def-slider__prev {
      top: -24px;
    }
    .def-slider__next {
      top: unset;
      bottom: -24px;
      right: unset;
    }
    .def-slider__default-button {
      height: 24px;
      background-color: transparent;
    }
  }
  &__container {
    position: relative;
    &_mb {
      margin-bottom: 16px;
    }
  }
  &__nav {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    cursor: pointer;

    -webkit-user-select: all; /* Chrome 49+ */
    -moz-user-select: all; /* Firefox 43+ */
    -ms-user-select: all; /* No support yet */
    user-select: all; /* Likely future */

    &_disabled {
      display: none;
    }
  }
  &__next {
    right: 18px;
  }
  &__prev {
    left: 18px;
  }
  &__default-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background-color: #fff;
    box-shadow: 0px -1px 3px 0px rgba(34, 45, 74, 0.04), 0px 2px 2px 0px rgba(34, 45, 74, 0.04),
      0px 2px 3px 0px rgba(34, 45, 74, 0.16);
    svg {
      display: block;
      width: 24px;
      height: 24px;
      fill: var(--background-black);
    }
  }
  &__pagination {
    margin-top: 16px;
    @include point-md {
      margin-top: 24px;
    }
  }
  .swiper-scrollbar {
    background: #f2f2f2;
    width: 100%;
    border-radius: 4px;
    height: 8px;
    position: static;
    margin-top: 16px;
  }
  .swiper-scrollbar-drag {
    background-color: var(--color-red);
    border-radius: 4px;
  }
  :deep(.swiper-slide) {
    height: unset;
  }
  .swiper-wrapper {
    align-self: stretch;
    width: 100%;
  }
  .swiper-pagination {
    position: relative;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    z-index: 1;
    height: 8px;
    //margin-bottom: 16px;
    //@include point-lg {
    //  margin-bottom: 24px;
    //}
  }
  :deep(.swiper-pagination-bullet) {
    width: 8px;
    height: 8px;
    background-color: var(--background-black);
    transition: 0.2s linear opacity;
    margin-left: 0;
    flex-shrink: 0;
    opacity: 0.2;
  }
  :deep(.swiper-pagination-bullet-active) {
    opacity: 1;
  }
  :deep(.swiper-container) {
    overflow: hidden;
  }
  :deep(.swiper-button-disabled) {
    display: none;
  }
}
</style>
